import React, { useCallback, useId } from 'react';
import { motion } from 'framer-motion';

interface ToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

// ✅ Static config (no useMemo needed)
const SIZE_CONFIG = {
  sm: { container: 'h-6 w-10', knob: 'h-4 w-4' },
  md: { container: 'h-6 w-11', knob: 'h-4 w-4' },
  lg: { container: 'h-7 w-14', knob: 'h-5 w-5' },
} as const;

const TEXT_SIZE = {
  sm: 'text-xs sm:text-sm',
  md: 'text-sm sm:text-base lg:text-lg',
  lg: 'text-base sm:text-lg lg:text-xl',
} as const;

// ✅ Reusable base classes
const BASE_TOGGLE_CLASSES = `
  relative inline-flex items-center 
  rounded-full shrink-0
  transition-colors duration-200
  focus-visible:outline-none 
  focus-visible:ring-2 
  focus-visible:ring-[var(--accent-color)] 
  focus-visible:ring-offset-2
`;

export const Toggle: React.FC<ToggleProps> = ({
  label,
  description,
  checked,
  onChange,
  size = 'md',
  disabled = false,
  className = '',
}) => {
  const id = useId();
  const descriptionId = description ? `${id}-desc` : undefined;
  const config = SIZE_CONFIG[size];

  // ✅ Optimized handler
  const handleToggle = useCallback(() => {
    if (!disabled) onChange(!checked);
  }, [checked, disabled, onChange]);

  // ✅ Respect reduced motion preference
  const shouldReduceMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  return (
    <div
      className={`
        flex flex-wrap items-center justify-between 
        gap-2 sm:gap-4 
        ${className}
      `}
    >
      {/* ✅ Text Section */}
      <div className="flex-1 min-w-0">
        <label
          htmlFor={id}
          className={`
            block font-medium 
            text-[var(--text-primary)] 
            ${TEXT_SIZE[size]} 
            break-words
          `}
        >
          {label}
        </label>

        {description && (
          <p
            id={descriptionId}
            className="
              text-xs sm:text-sm 
              text-[var(--text-secondary)] 
              mt-0.5
              line-clamp-2
            "
          >
            {description}
          </p>
        )}
      </div>

      {/* ✅ Toggle Switch */}
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        aria-describedby={descriptionId}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        className={`
          ${BASE_TOGGLE_CLASSES}
          ${config.container}

          ${
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer'
          }

          ${
            checked
              ? 'bg-[var(--accent-color)]'
              : 'bg-gray-300 dark:bg-gray-600'
          }
        `}
      >
        {/* ✅ Animated Knob */}
        <motion.span
          className={`
            inline-block 
            ${config.knob}
            rounded-full 
            bg-white 
            shadow-md
            ml-0.5
          `}
          animate={{
            x: checked ? 'calc(100% + 2px)' : '0%',
          }}
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : {
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }
          }
          whileTap={!disabled && !shouldReduceMotion ? { scale: 0.95 } : {}}
        />
      </button>
    </div>
  );
};
